import cn from "@/v2-ui/utils/utils.cn"
import { summarizeAttributeKeys } from "@/v2-backoffice-client/translation/attributes/translation.attributes.utils"
import type { TranslationRow } from "@/v2-backoffice-client/translation/translation.types"

export type TranslationAttributesHintProps = {
  className?: string
  /** Every value of the set this row belongs to, the row itself included. */
  siblingRows: TranslationRow[]
}

/**
 * What the rest of this type set puts in its Attributes column: each key with
 * how many values carry it and the values actually in use.
 *
 * Nothing declares the shape a type table's attributes must have — the column
 * is free JSON and the keys are the columns of the table the seed writes into
 * — so the set's own rows are the only description of it there is. Showing it
 * beside the editor is what turns "type valid JSON" into "match the four keys
 * the other twelve values here use", which is the difference between a value
 * that seeds and one that does not.
 */
function TranslationAttributesHint(props: TranslationAttributesHintProps) {
  const { siblingRows, className } = props

  const keys = summarizeAttributeKeys(siblingRows)
  const withAttributes = siblingRows.filter((row) => row.attributes).length

  if (keys.length === 0) {
    return (
      <p className={cn("text-xs text-muted-foreground", className)}>
        No other value in this set has attributes, so there is no established
        shape to match. Leave <code>{"{}"}</code> unless this type table has
        columns beyond Name and Description.
      </p>
    )
  }

  return (
    <div className={cn("space-y-1 text-xs text-muted-foreground", className)}>
      <p>
        Used by {withAttributes} value{withAttributes === 1 ? "" : "s"} in this
        set:
      </p>
      <ul className="space-y-0.5">
        {keys.map((usage) => (
          <li key={usage.key}>
            <code>{usage.key}</code>
            {" — "}
            {usage.examples.join(", ")}
            {usage.hasMoreExamples && ", …"}
            {usage.count < withAttributes && (
              <span> (on {usage.count} of them)</span>
            )}
          </li>
        ))}
      </ul>
    </div>
  )
}

export default TranslationAttributesHint
